import React, { useState, useEffect, useMemo } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import {
  DownloadIcon,
  CheckIcon
} from '@heroicons/react/solid';
import { functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { analyzePatientDataForExport } from '../utils/exportUtils';

export default function ExportWizardDialog({ isOpen, onClose, patientDataModel, patientId, informantData = {} }) {
  const [selectedCategories, setSelectedCategories] = useState({
    patientHistory: true,
    patientScales: true,
    informantScales: true,
    informantHistory: false,
  });
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Determine assessment pathway — Autism patients get 4 categories with
  // scales hard-disabled; adult ADHD patients keep the existing 3 categories.
  // CYP ADHD patients get a single category: the child completes no
  // questionnaires themselves, so the only exportable data is what the
  // parent/carer and school representative completed (served by the modular
  // informantHistory export, which includes their history questionnaires and
  // SNAP-IV rating scales).
  const isAutism = patientDataModel?.assessmentModule === 'adultAutism';
  const isCypAdhd = patientDataModel?.assessmentModule === 'cypAdhd';

  // Analyze data for export
  const dataAnalysis = useMemo(() => {
    return analyzePatientDataForExport(patientDataModel, informantData);
  }, [patientDataModel, informantData]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setErrorMessage('');
      setSelectedFormat(null);
      
      // Set initial category selection based on available data and pathway.
      // Patient scales are selected whenever the patient actually has scale data
      // (autism orgs like LMP assign scales; TOC autism has none, so this stays
      // false and the layout is unchanged). Informant history is Autism-only;
      // informant scales remain Autism-disabled (no autism informant scales).
      // CYP ADHD: only informantHistory applies — the child has no forms of
      // their own, and the legacy informantScales export path does not read
      // modular informant records.
      setSelectedCategories({
        patientHistory: isCypAdhd ? false : dataAnalysis.patientHistory.hasData,
        patientScales: isCypAdhd ? false : dataAnalysis.patientScales.hasData,
        informantHistory: (isAutism || isCypAdhd) ? dataAnalysis.informantHistory.hasData : false,
        informantScales: (isAutism || isCypAdhd) ? false : dataAnalysis.informantScales.hasData,
      });
    }
  }, [isOpen, dataAnalysis, isAutism, isCypAdhd]);

  const handleCategoryToggle = (category, forceDisabled) => {
    if (forceDisabled) return;
    setSelectedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const hasSelectedCategories = Object.values(selectedCategories).some(Boolean);
  const canDownload = hasSelectedCategories && selectedFormat;

  const handleExport = async () => {
    if (!canDownload) return;
    
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      // Call the export cloud function
      const exportPatientDataFn = httpsCallable(functions, 'exportPatientData');
      
      const result = await exportPatientDataFn({
        patientId,
        clinicianId: patientDataModel?.createdBy || patientDataModel?.clinicians?.clinicianCI?.clinicianId,
        selectedCategories,
        format: selectedFormat,
        assessmentModule: patientDataModel?.assessmentModule || 'adultAdhd',
      });
      
      if (result.data.status === 'success') {
        // Trigger download
        const downloadUrl = result.data.downloadUrl;
        const link = document.createElement('a');
        link.href = downloadUrl;
        
        // Generate filename to match backend format
        const today = new Date();
        const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
        const firstName = patientDataModel?.firstName || '';
        const lastName = patientDataModel?.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim() || 'Unknown Patient';
        const fileExtension = selectedFormat === 'pdf' ? 'pdf' : 'xlsx';
        
        link.download = `Patient Data Export - ${fullName} - ${dateStr}.${fileExtension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Close dialog
        onClose();
      } else {
        setErrorMessage(result.data.message || 'Export failed. Please try again.');
      }
      
    } catch (error) {
      console.error('Export failed:', error);
      let errorMsg = 'Export failed. Please try again.';
      
      if (error.code === 'functions/permission-denied') {
        errorMsg = 'You do not have permission to export this patient\'s data.';
      } else if (error.code === 'functions/not-found') {
        errorMsg = 'Patient data not found.';
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };


  // Build pathway-specific category options. Autism patients see 4 categories
  // with scales hard-disabled ("Not applicable"); ADHD keeps the existing 3.
  // CYP ADHD keeps a single checkbox (consistent with Autism's single
  // "Informant History" checkbox) but derives the label dynamically from
  // which informant roles exist on the patient, and shows a per-informant
  // summary so clinicians know exactly what they are exporting.
  const getCategoryOptions = () => {
    if (isCypAdhd) {
      // Determine which CYP informant roles are linked to this patient.
      // The school rep only appears in the informants map after their account
      // is created (nomination pending/skipped means guardian only).
      const informantsMap = patientDataModel?.informants || {};
      const hasGuardian = Object.values(informantsMap).some(
        (entry) => entry?.informantRole === 'legalGuardian',
      );
      const hasSchoolRep = Object.values(informantsMap).some(
        (entry) => entry?.informantRole === 'schoolRep',
      );

      // Dynamic label: reflects which informant roles actually exist so the
      // clinician is never misled about what the export will contain.
      let cypLabel;
      if (hasGuardian && hasSchoolRep) {
        cypLabel = 'Parent/carer and school representative questionnaires';
      } else if (hasGuardian) {
        cypLabel = 'Parent/carer questionnaires';
      } else if (hasSchoolRep) {
        cypLabel = 'School representative questionnaires';
      } else {
        cypLabel = 'Informant questionnaires';
      }

      // Per-informant summary: derived from existing analysis data (each form
      // entry carries informantId) cross-referenced with the patient header's
      // informants map (which carries informantRole and informantName). This
      // is a pure derivation — zero changes to exportUtils.js.
      const informantSummary = Object.entries(informantsMap)
        .filter(([, entry]) => entry?.informantRole)
        .map(([id, entry]) => ({
          name: entry.informantName || 'Informant',
          role: entry.informantRole === 'schoolRep'
            ? 'School representative'
            : 'Parent/carer',
          formCount: dataAnalysis.informantHistory.forms.filter(
            (f) => f.informantId === id,
          ).length,
        }))
        .filter((s) => s.formCount > 0);

      return [
        {
          key: 'informantHistory',
          label: cypLabel,
          count: dataAnalysis.informantHistory.count,
          hasData: dataAnalysis.informantHistory.hasData,
          statusCounts: dataAnalysis.informantHistory.statusCounts,
          forceDisabled: false,
          // Only present for CYP; the render loop uses this to show the
          // per-informant breakdown beneath the checkbox label.
          informantSummary,
        },
      ];
    }
    if (isAutism) {
      return [
        {
          key: 'patientHistory',
          label: 'Patient History Questionnaires',
          count: dataAnalysis.patientHistory.count,
          hasData: dataAnalysis.patientHistory.hasData,
          statusCounts: dataAnalysis.patientHistory.statusCounts,
          forceDisabled: false,
        },
        {
          // Autism orgs that assign rating scales (e.g. LMP) can export them;
          // orgs with no scales (e.g. TOC) still see this as "Not applicable".
          key: 'patientScales',
          label: 'Patient Rating Scales',
          count: dataAnalysis.patientScales.count,
          hasData: dataAnalysis.patientScales.hasData,
          statusCounts: dataAnalysis.patientScales.statusCounts,
          forceDisabled: !dataAnalysis.patientScales.hasData,
          disabledReason: 'Not applicable',
        },
        {
          key: 'informantHistory',
          label: 'Informant History Questionnaires',
          count: dataAnalysis.informantHistory.count,
          hasData: dataAnalysis.informantHistory.hasData,
          statusCounts: dataAnalysis.informantHistory.statusCounts,
          forceDisabled: false,
        },
        {
          key: 'informantScales',
          label: 'Informant Rating Scales',
          count: 0,
          hasData: false,
          statusCounts: dataAnalysis.informantScales.statusCounts,
          forceDisabled: true,
          disabledReason: 'Not applicable',
        },
      ];
    }
    // ADHD — existing 3 categories, no forceDisabled
    return [
      {
        key: 'patientHistory',
        label: 'Patient History',
        count: dataAnalysis.patientHistory.count,
        hasData: dataAnalysis.patientHistory.hasData,
        statusCounts: dataAnalysis.patientHistory.statusCounts,
        forceDisabled: false,
      },
      {
        key: 'patientScales',
        label: 'Patient Self-Report Scales',
        count: dataAnalysis.patientScales.count,
        hasData: dataAnalysis.patientScales.hasData,
        statusCounts: dataAnalysis.patientScales.statusCounts,
        forceDisabled: false,
      },
      {
        key: 'informantScales',
        label: 'Informant Scales',
        count: dataAnalysis.informantScales.count,
        hasData: dataAnalysis.informantScales.hasData,
        statusCounts: dataAnalysis.informantScales.statusCounts,
        forceDisabled: false,
      },
    ];
  };

  const formatOptions = [
    { key: 'pdf', label: 'PDF Document' },
    { key: 'excel', label: 'Excel Spreadsheet' }
  ];

  // Helper function to show only when no data is available
  const getDataStatusDisplay = (hasData) => {
    return hasData ? null : "No data available";
  };


  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
          
          {/* Header */}
          <AlertDialog.Title className="text-xl font-semibold mb-4">
            Export Form Data
          </AlertDialog.Title>
          
          <AlertDialog.Description className="text-sm text-gray-500 mb-4">
            Select which data to export and choose your preferred format.
            <br />
            <span className="text-xs text-gray-400 mt-1 block">
              Note: All forms (completed, started, and not started) will be included. 
              Unanswered questions will show "No answer given".
            </span>
          </AlertDialog.Description>

          <VisuallyHidden.Root>
            <AlertDialog.Description>
              Export patient form data dialog
            </AlertDialog.Description>
          </VisuallyHidden.Root>

          <div className="space-y-6">
            {/* Data Categories */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Data to Export
              </label>
              <div className="grid grid-cols-1 gap-3">
                        {getCategoryOptions().map(category => {
                          const isDisabled = category.forceDisabled || !category.hasData;
                          const badgeText = category.forceDisabled
                            ? category.disabledReason
                            : getDataStatusDisplay(category.hasData);

                          return (
                            <button
                              key={category.key}
                              type="button"
                              onClick={() => !isDisabled && handleCategoryToggle(category.key, category.forceDisabled)}
                              disabled={isDisabled}
                              className={`flex items-start px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-left ${
                                selectedCategories[category.key] && !isDisabled
                                  ? 'bg-violet-100 text-violet-800 border border-violet-300 shadow-sm'
                                  : !isDisabled
                                    ? 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                                    : 'bg-gray-50 text-gray-400 border border-gray-100 cursor-not-allowed'
                              }`}
                            >
                              {/* Checkbox — aligned to the top of the first label line */}
                              <div className={`w-4 h-4 rounded border-2 mr-3 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                                selectedCategories[category.key] && !isDisabled
                                  ? 'bg-violet-600 border-violet-600'
                                  : !isDisabled
                                    ? 'border-gray-300 bg-white'
                                    : 'border-gray-200 bg-gray-50'
                              }`}>
                                {selectedCategories[category.key] && !isDisabled && (
                                  <CheckIcon className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <span>{category.label}</span>
                                  {badgeText && (
                                    <span className="ml-auto flex-shrink-0 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">
                                      {badgeText}
                                    </span>
                                  )}
                                </div>
                                {/* Per-informant summary (CYP ADHD only): shows each
                                    informant's name, role, and form count so the
                                    clinician knows exactly who is included. */}
                                {category.informantSummary && category.informantSummary.length > 0 && (
                                  <div className="mt-1 space-y-0.5">
                                    {category.informantSummary.map((inf, idx) => (
                                      <div key={idx} className="text-xs font-normal text-gray-500">
                                        {inf.name} ({inf.role}) &mdash; {inf.formCount} {inf.formCount === 1 ? 'form' : 'forms'}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
              </div>
            </div>

            <div className="h-px bg-gray-200" aria-hidden="true" />

            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Export Format
              </label>
              <div className="grid grid-cols-1 gap-3">
                {formatOptions.map(format => (
                  <button
                    key={format.key}
                    type="button"
                    onClick={() => setSelectedFormat(format.key)}
                    className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      selectedFormat === format.key
                        ? 'bg-violet-100 text-violet-800 border border-violet-300 shadow-sm'
                        : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                    }`}
                  >
                    {/* Radio Button */}
                    <div className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                      selectedFormat === format.key
                        ? 'border-violet-600'
                        : 'border-gray-300'
                    }`}>
                      {selectedFormat === format.key && (
                        <div className="w-2 h-2 rounded-full bg-violet-600"></div>
                      )}
                    </div>
                    {format.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {errorMessage && (
            <div className="p-3 mt-4 text-sm font-semibold text-red-600 bg-red-50 rounded-md">
              {errorMessage}
            </div>
          )}

          {/* PDF Warning Message - appears when PDF is selected */}
          {selectedFormat === 'pdf' && (
            <div className="mt-4 transition-all duration-300 ease-in-out">
              <p className="text-xs text-violet-600">
                PDF exports can take up to one minute. Please do not refresh the page.
              </p>
            </div>
          )}          

          {/* Actions */}
          <div className="mt-6 flex justify-between">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </AlertDialog.Cancel>

            <button
              type="button"
              onClick={handleExport}
              disabled={!canDownload || isLoading}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                !canDownload || isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-violet-600 hover:bg-violet-700'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                <div className="flex items-center">
                  <DownloadIcon className="h-4 w-4 mr-2" />
                  Download
                </div>
              )}
            </button>
          </div>

        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
